/** @jsxImportSource @emotion/react */
import { createElement } from 'react';
import { AnimatePresence, type Variants, type Easing, circOut } from 'framer-motion';
import { ClickableLogo, LogoWrapper } from './shared.styled';
import logoLight from '/main-logo-white.svg';
import logoDark from '/main-logo-white.svg';

import { StepProgress } from '../../components/step-progress/step-progress';
import type { StepKey } from '../../components/step-progress/step-progress';

import { Wrapper, Container, ShellLogoImg } from './registration.styled';
import {
  useRegistrationShell,
  type RegistrationShellProps,
  type OnboardingStepProps,
} from './registration.hooks';
import { VSpace } from '../chat/settings/account/account-settings';

/* re-export to preserve external API */
export type { OnboardingStepProps } from './registration.hooks';

/* ───────────────── logo ───────────────── */
export const ShellLogo = ({ isLight, small }: { isLight: boolean; small: boolean }) => {
  return (
    <ClickableLogo data-testid="onboarding-logo">
      <LogoWrapper data-testid="onboarding-logo-wrapper">
        <ShellLogoImg
          src={isLight ? logoDark : logoLight}
          alt="Mailander logo"
          small={small}
          data-testid="onboarding-logo-img"
        />
      </LogoWrapper>
    </ClickableLogo>
  );
};

/* ───────────────── animation ───────────────── */
const easeOut: Easing = [0.17, 0.67, 0.83, 0.67];
const variants: Variants = {
  initial: { x: 50, opacity: 0 },
  animate: { x: 0, opacity: 1, transition: { duration: 0.5, ease: easeOut } },
  exit: { x: -50, opacity: 0, transition: { duration: 0.4, ease: circOut } },
};

/* ───────────────── shell ───────────────── */
export const RegistrationShell: React.FC<RegistrationShellProps> = ({ steps }) => {
  const {
    isLight,
    index,
    Step,
    background,
    fill,
    progressKey,
    smallLogo,
    goNext,
    goPrev,
    goTo,
    showLogo,
    containerAlign,
    containerJustify,
  } = useRegistrationShell(steps);
  return (
    <Wrapper bg={background} $fill={fill} data-testid="onboarding-wrapper" data-step-index={index}>
      {/* optional top progress bar */}
      <VSpace $h={16} />
      {progressKey !== false && (
        <StepProgress
          current={(progressKey ?? 'Setup') as StepKey}
          data-testid="onboarding-progress"
        />
      )}
      <VSpace $h={24} />
      {showLogo && <ShellLogo isLight={isLight} small={smallLogo} />}

      <AnimatePresence mode="wait">
        <Container
          key={index}
          variants={variants}
          initial="initial"
          animate="animate"
          exit="exit"
          data-testid="onboarding-step-container"
          $align={containerAlign}
          $justify={containerJustify}
        >
          {createElement<OnboardingStepProps>(Step, {
            goNext,
            goPrev,
            goTo,
            stepIndex: index,
          })}
        </Container>
      </AnimatePresence>
    </Wrapper>
  );
};
